import { useRef, useEffect } from "react";
import Button from "../../../components/form-controls/Button";
import Input from "../../../components/form-controls/Input";
import { Controller, FormProvider, useFieldArray, useForm } from "react-hook-form";
import Tags from "../../../components/Tags";
import InvalidFeedback from "../../../components/InvalidFeedback";
import FieldForm from "./FieldForm";
import PreviewField from "./PreviewField";
import { FormService } from "../../../services/FormsService";
import { AlertService } from "../../../services/Alert";
import { useCurrentAccount } from "../../../hooks/useCurrentAccount";
import useModal from "../../../hooks/useModal";
import { useTranslation } from 'react-i18next';
const expressionToURL = /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})|(http:\/\/localhost)./gi;

interface FormModalProps {
    show: boolean;
    onRequestClose: (reload: boolean) => void;
    data?: any;
}
function FormModal({ show, onRequestClose, data }: FormModalProps) {

    const { t }= useTranslation();
    
    const { account } = useCurrentAccount();
    
    const modalSubRef = useRef<HTMLDivElement>(null);

    useModal(modalSubRef, show)

    const defaultValueKeys = {
        type: 'text',
        name: '',
        options: [],
        mandatory: false,

        dateConfig: {
            isLimited: false,
            min: 1,
            max: 1
        }
    }

    const defaultValues = {
        form_id: 0,
        name: '',
        categories: [],
        webhook: '',
        keys: []
    }

    const methods = useForm({
        mode: 'onChange',
        defaultValues
    });

    const { fields: keys, append, remove } = useFieldArray({
        control: methods.control, 
        name: "keys", 
    });

    const submit = async (data) => {
        try {
            data.account_id = account.id
            data.categories = data.categories.join(',')
            data.keys = data.keys.map( k => {
                k.mandatory = k.mandatory ? 1: 0;
                k.options = k.options ? k.options.join(','): ''
                return k;
            })

            if(data.form_id) {
                await FormService.update(data);
            } else {
                delete data.form_id;
                await FormService.save(data);
            }
            
            AlertService.success('¡Éxito!', 'Se guardó el formulario correctamente');
            await closeAndReset(true)
        } catch (error) {
            console.error(error);
            AlertService.error('¡Error!', 'Ocurrió un error al guardar el formulario, intenta nuevamente')
        }
    }

    const closeAndReset = async (reload: boolean) => {
        await onRequestClose(reload);
        methods.reset(defaultValues);
    }

    const addField = () => {
        append(defaultValueKeys)
    }

    useEffect(() => {
        if(data) {
            methods.reset(data)
        }
    }, [data]);

    return (
        <FormProvider {...methods}>
            <div className="modal fade" ref={modalSubRef} data-bs-backdrop="static" data-bs-keyboard="false">
                <div className="modal-dialog _modal-dialog-scrollable_ modal-xl" role="document">
                    <form onSubmit={methods.handleSubmit(submit)}>
                        <div className="modal-content">
                            <div className="modal-header">
                                <h4 className="modal-title">{t("config.forms.form-modal.title")}</h4>
                                <button type="button" className="btn-close" onClick={() => closeAndReset(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-7">
                                        <div className="mb-3">
                                            <Input label={t("config.forms.form-modal.name-title")} errors={methods.formState.errors.name} {...methods.register('name', { required: true })} />
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label" >{t("config.forms.form-modal.category")}</label>
                                            <Controller
                                                control={methods.control}
                                                name='categories'
                                                rules={{ required: true }}
                                                render={({
                                                    field: { onChange, value },
                                                    fieldState: { error },
                                                }) => {
                                                    return (
                                                        <Tags
                                                            isInvalid={error ? true : false}
                                                            onChange={(values: any) => onChange(values)}
                                                            initialValue={value.map(v => ({ value: v, label: v }))}
                                                        />
                                                    )
                                                }}
                                            />
                                            <InvalidFeedback error={methods.formState.errors.categories} />
                                        </div>

                                        <hr />
                                        <div className="mb-3">
                                            <label className="form-label mb-0" >WebHook <span className="form-label-secondary">({t("optional-title")})</span></label>
                                            <p className="form-text mt-1 mb-2">
                                                {t("config.forms.form-modal.webhook-text")}
                                            </p>
                                            <Input label='URL' errors={methods.formState.errors.webhook} {...methods.register('webhook', { pattern: { value: new RegExp(expressionToURL), message: 'Debe ingresar una URL válida' } })} />
                                        </div>

                                        <hr />

                                        <div className="mb-3">
                                            <label className="form-label mb-0" >{t("config.forms.form-modal.field-form-title")} <span className="form-label-secondary">({t("optional-title")})</span></label>
                                            <p className="form-text mt-1 mb-2">
                                                {t("config.forms.form-modal.field-form-text")}
                                            </p>
                                        </div>

                                        <hr />

                                        {
                                            keys.map( (k, i) => (
                                                <FieldForm 
                                                    key={k.id}
                                                    index={i}
                                                    delete={() => remove(i)}
                                                />
                                            ))
                                        }

                                        <div>
                                            <button type="button" className="btn btn-link px-0" onClick={addField}><i className="bi-plus"></i> {t("config.forms.form-modal.add-field")}</button>
                                        </div>
                                    </div>
                                    <div className="col-5 bg-light p-3 rounded shadow-sm border">
                                        <div className="">
                                            <h4>{t("config.forms.form-modal.side-preview.preview-title")}</h4>
                                            <p>{t("config.forms.form-modal.side-preview.preview-text")}</p>
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label" >{t("config.forms.form-modal.side-preview.form-label")}</label>
                                            <select className="form-control">
                                                <option>{methods.watch('name') || `${t("config.forms.form-modal.side-preview.empty-name-from")}`}</option>
                                            </select>
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label" >{t("config.forms.form-modal.side-preview.customer-name")}</label>
                                            <input type="text" className="form-control gb-white-force" readOnly defaultValue="John Doe" />
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label" >Folio *</label>
                                            <input type="text" className="form-control gb-white-force" readOnly defaultValue="12345" />
                                        </div>

                                        {
                                            methods.watch('keys').map( (k, i) => <PreviewField key={i} {...k} />)
                                        }

                                        <div className="mb-3">
                                            <label className="form-label text-nowrap mb-0" >{t("config.forms.form-modal.side-preview.status")}</label>
                                            <select className="form-control">
                                                { methods.watch('categories').length === 0 && <option >{t("config.forms.form-modal.side-preview.empty-category")}</option>}
                                                {methods.watch('categories').map( (c,i) =><option key={i}>{c}</option> )}
                                            </select>
                                        </div>
                                    </div>
                                </div>

                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-white btn-sm" onClick={() => closeAndReset(false)}>{t("btn-cancel")}</button>
                                <Button type="submit" className="btn btn-primary btn-sm" disabled={!methods.formState.isValid} isLoading={methods.formState.isSubmitting}>{t("btn-save")}</Button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </FormProvider>
    )
}
export default FormModal